import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Ported from careers.co.jp v2 Button — theme-aware variants, scale-on-press.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5",
    "whitespace-nowrap font-semibold tracking-tight",
    "transition-[background-color,color,transform] duration-150 ease-out",
    "active:scale-[0.97]",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2",
    "disabled:opacity-50 disabled:pointer-events-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: "bg-brand-500 text-text-on-brand hover:bg-brand-600 active:bg-brand-700",
        secondary:
          "bg-surface-2 text-text border border-border-2 hover:bg-surface-3 hover:border-border-2",
        ghost: "bg-transparent text-text hover:bg-surface-2",
        danger: "bg-danger text-text-on-brand hover:opacity-90",
        success: "bg-success text-text-on-brand hover:opacity-90",
        link: "bg-transparent text-brand-on underline underline-offset-4 hover:text-brand-500",
      },
      size: {
        sm: "h-8 px-3 text-[13px] rounded-sm",
        md: "h-9 px-3.5 text-sm rounded-sm",
        lg: "h-11 px-5 text-[15px] rounded-md",
        icon: "h-9 w-9 rounded-full",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = "Button";
